import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n';

export interface BreadcrumbItem {
  label: string;
  to?: string;
  path?: string;
  href?: string;
}

export interface AdminPageHeaderBadge {
  label: string;
  bg?: string;
  color?: string;
  border?: string;
}

export interface AdminPageHeaderProps {
  breadcrumbs?: BreadcrumbItem[];
  icon?: React.ReactNode;
  title: string;
  badge?: string | AdminPageHeaderBadge;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  maxWidth?: string;
}

export const BREADCRUMB_MAP: Record<string, string> = {
  'home': 'breadcrumb.home',
  'dashboard': 'breadcrumb.dashboard',
  'admin': 'breadcrumb.admin',
  'learner portal': 'breadcrumb.learner_portal',
  'administration console': 'nav.admin_console',
  'admin overview': 'nav.admin_console',
  'simulation attempts': 'breadcrumb.simulation_attempts',
  'my simulation attempts': 'breadcrumb.my_simulation_attempts',
  'my attempts': 'breadcrumb.my_attempts',
  'attempt history': 'breadcrumb.attempt_history',
  'assigned simulations': 'dashboard.assigned_simulations',
  'scenario & case studio': 'breadcrumb.scenarios_studio',
  'scenario & simulation studio': 'breadcrumb.scenarios_studio',
  'scenarios': 'breadcrumb.scenarios',
  'scenarios studio': 'breadcrumb.scenarios_studio',
  'scenario details': 'breadcrumb.scenario_details',
  'create scenario': 'breadcrumb.create_scenario',
  'edit scenario': 'breadcrumb.edit_scenario',
  'user directory': 'breadcrumb.user_directory',
  'learners & users': 'breadcrumb.learners_users',
  'client organizations': 'breadcrumb.client_organizations',
  'organizations': 'breadcrumb.client_organizations',
  'platform organizations & tenants': 'breadcrumb.client_organizations',
  'organization details': 'breadcrumb.org_details',
  'evaluation rubrics': 'breadcrumb.evaluation_rubrics',
  'evaluation configuration': 'breadcrumb.evaluation_config',
  'evaluation studio': 'breadcrumb.evaluation_studio',
  'reports & analytics': 'breadcrumb.reports_analytics',
  'reports': 'breadcrumb.reports',
  'analytics console': 'breadcrumb.analytics',
  'analytics': 'breadcrumb.analytics',
  'audit log': 'breadcrumb.audit_log',
  'audit trail': 'breadcrumb.audit_trail',
  'security audit trail': 'breadcrumb.audit_trail',
  'llm audit trail': 'breadcrumb.llm_audit_trail',
  'llm audit & logs': 'breadcrumb.llm_logs',
  'tenant settings': 'breadcrumb.tenant_settings',
  'notification settings': 'breadcrumb.notifications',
  'custom user fields': 'breadcrumb.custom_fields',
  'languages & regional locales': 'admin.languages_title',
  'language settings': 'admin.languages_title',
  'package management': 'breadcrumb.packages',
  'results': 'scorecard.title',
  'evaluation scorecard': 'scorecard.title',
};

export const TITLE_MAP: Record<string, string> = {
  'trainova ai administration console': 'admin.console_title',
  'assigned simulations & practice studio': 'dashboard.assigned_studio',
  'simulation attempts & performance ledger': 'attempts.title_admin',
  'my simulation attempts & history': 'attempts.title_learner',
  'scenario & simulation studio': 'scenarios.studio_title',
  'organization members & user directory': 'users.directory_title',
  'platform organizations & tenants': 'tenants.title',
  'enterprise audit trail & governance logs': 'audit.title',
  'llm engine logs & model inference auditing': 'llm_logs.title',
  'enterprise reports & cohort analytics': 'reports.title',
  'analytics & practice intelligence': 'analytics.title',
  'assessment evaluation scorecard': 'scorecard.title',
  'languages & regional locales': 'admin.languages_title',
  'custom user profile fields (udf)': 'breadcrumb.custom_fields',
};

export const BADGE_MAP: Record<string, string> = {
  'enterprise control plane': 'badge.enterprise_control_plane',
  'roleplay hub': 'badge.roleplay_hub',
  'practice ledger': 'badge.practice_ledger',
  'admin practice ledger': 'badge.admin_practice_ledger',
  'simulation authoring': 'badge.simulation_authoring',
  'access & governance': 'badge.access_governance',
  'multi-tenant master': 'badge.multi_tenant_master',
  'evaluation governance': 'badge.evaluation_governance',
  'executive intelligence': 'badge.executive_intelligence',
  'security & compliance': 'badge.security_compliance',
  'ai observability': 'badge.ai_observability',
};

export const DESCRIPTION_MAP: Record<string, string> = {
  'select an assessment scenario to begin or continue your conversational roleplay': 'desc.select_scenario',
  'monitor learner simulations across organizations, inspect attempt scorecards, and audit historical practice outcomes.': 'desc.attempts_admin',
  'track your simulation sessions, resume active scenarios, and review past practice scorecards.': 'desc.attempts_learner',
  'author, test, and publish conversational ai simulations, customer personas, and evaluation rubrics.': 'desc.scenarios',
  'centralized management of enterprise client organizations, tenant provisioning, and platform governance.': 'desc.tenants',
  'manage learner accounts, assign instructor and manager permissions, and oversee cohort access.': 'desc.users',
  'inspect immutable compliance events, administrative mutations, and authentication history.': 'desc.audit',
  'audit llm prompts, input/output token consumptions, model latencies, costs, and raw payloads per attempt and scenario.': 'desc.llm_audit',
  'review organization pass rates, competency benchmark bars, learner metrics, csv/json data dumps.': 'desc.reports',
  'real-time simulation completion metrics, competency pass rates, learner drilldowns, and multi-tenant performance.': 'desc.analytics',
  'system-wide governance, tenant administration, training metrics, and ai simulation studio.': 'desc.admin_dashboard',
  'configure dialogue llms, scoring engines, stt/tts voice models, and encrypted api keys.': 'desc.llm_config',
  'configure learner metadata fields (region, cost center, bu), csv validation rules, and api sync mappings.': 'desc.udf_settings',
  'configure evaluation rubrics, scoring prompts, golden set calibration, and publishing lifecycles.': 'desc.eval_config',
};

export const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  breadcrumbs,
  icon,
  title,
  badge,
  description,
  actions,
  children,
  className = '',
  style,
  maxWidth,
}) => {
  const { t } = useTranslation();
  const badgeConfig = typeof badge === 'string'
    ? { label: badge }
    : badge;

  const translateBreadcrumb = (label: string): string => {
    const key = BREADCRUMB_MAP[label.trim().toLowerCase()];
    if (key) return t(key, undefined, label);
    return t(label, undefined, label);
  };

  const translateTitle = (rawTitle: string): string => {
    const key = TITLE_MAP[rawTitle.trim().toLowerCase()];
    if (key) return t(key, undefined, rawTitle);
    return t(rawTitle, undefined, rawTitle);
  };

  const translateBadge = (rawBadge?: string): string => {
    if (!rawBadge) return '';
    const key = BADGE_MAP[rawBadge.trim().toLowerCase()];
    if (key) return t(key, undefined, rawBadge);
    return t(rawBadge, undefined, rawBadge);
  };

  const translateDescription = (rawDesc: React.ReactNode): React.ReactNode => {
    if (typeof rawDesc !== 'string') return rawDesc;
    const trimmed = rawDesc.trim();
    if (trimmed.includes('•')) {
      const parts = trimmed.split('•');
      const prefix = parts[0].trim();
      const suffix = parts.slice(1).join('•').trim();
      const prefixKey = DESCRIPTION_MAP[prefix.toLowerCase()];
      if (prefixKey) {
        return `${t(prefixKey, undefined, prefix)} • ${suffix}`;
      }
    }
    const key = DESCRIPTION_MAP[trimmed.toLowerCase()];
    if (key) return t(key, undefined, trimmed);
    return t(trimmed, undefined, trimmed);
  };

  return (
    <div
      className={`admin-page-header-container ${className}`}
      style={{
        maxWidth: maxWidth || undefined,
        width: '100%',
        marginBottom: '24px',
        ...style,
      }}
    >
      {/* 1. Standardized Breadcrumb Bar */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="admin-page-header-breadcrumbs" aria-label="Breadcrumb" style={{ marginBottom: '14px' }}>
          <ol
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              listStyle: 'none',
              padding: 0,
              margin: 0,
              fontSize: '13px',
              flexWrap: 'wrap',
            }}
          >
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              const linkTarget = crumb.to || crumb.path || crumb.href;
              const label = translateBreadcrumb(crumb.label);
              return (
                <React.Fragment key={`${crumb.label}-${idx}`}>
                  <li>
                    {linkTarget && !isLast ? (
                      <Link
                        to={linkTarget}
                        style={{
                          color: 'var(--muted, #64748B)',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.15s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--blue, #2563EB)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted, #64748B)')}
                      >
                        {label}
                      </Link>
                    ) : (
                      <span
                        style={{
                          color: isLast ? 'var(--navy, #0F172A)' : 'var(--muted, #64748B)',
                          fontWeight: isLast ? 700 : 500,
                        }}
                      >
                        {label}
                      </span>
                    )}
                  </li>
                  {!isLast && (
                    <li style={{ color: 'var(--muted, #94A3B8)', userSelect: 'none' }}>›</li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
        </nav>
      )}

      {/* 2. Unified High-Polish Gradient Hero Banner */}
      <div
        className="admin-page-header-banner"
        style={{
          background: 'linear-gradient(135deg, rgb(11, 25, 44) 0%, rgb(30, 62, 98) 100%)',
          borderRadius: 'clamp(12px, 2vw, 16px)',
          padding: 'clamp(16px, 4vw, 26px) clamp(16px, 4vw, 32px)',
          color: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          boxShadow: '0 8px 24px rgba(11, 25, 44, 0.15)',
        }}
      >
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
            {icon && (
              <span style={{ fontSize: '22px', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                {icon}
              </span>
            )}
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(17px, 4.5vw, 24px)',
                fontWeight: 800,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {translateTitle(title)}
            </h1>
            {badgeConfig && (
              <span
                style={{
                  background: badgeConfig.bg || 'rgba(59, 130, 246, 0.3)',
                  color: badgeConfig.color || '#93C5FD',
                  border: badgeConfig.border || '1px solid rgba(147, 197, 253, 0.3)',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '3px 9px',
                  borderRadius: '6px',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {translateBadge(badgeConfig.label)}
              </span>
            )}
          </div>
          {description && (
            <p
              className="admin-page-header-desc"
              style={{
                margin: 0,
                fontSize: 'clamp(12px, 2.5vw, 13.5px)',
                color: 'rgb(203, 213, 225)',
                maxWidth: '820px',
                lineHeight: 1.55,
              }}
            >
              {translateDescription(description)}
            </p>
          )}
        </div>

        {/* 3. Right-hand side Action / Filtering controls */}
        {(actions || children) && (
          <div
            className="admin-page-header-actions"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            {actions}
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
